/* ============================================================
 * src/screens/auth/_bundle.tsx
 *
 * Mechanical ESM transcription of the v.3 prototype's screens-auth.jsx.
 * The prototype destructured React from a global and exposed each screen
 * via Object.assign(window, ...). This file restores the same surface as
 * named ESM exports; per-component files in this directory re-export the
 * screen the route table maps to so downstream wiring tasks can edit a
 * single component without touching the bundle.
 * ============================================================ */
/* eslint-disable react-refresh/only-export-components, @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuthActions } from '@convex-dev/auth/react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Icon, Btn, Pill, CircaTag, Panel, PageHead, Breadcrumbs, Tabs, Spark,
  StatCard, ScoreRing, HelpDot, TimerChip, AccessGate, AdminGate, CapPill,
  FreshnessBanner, AuditTrailLink, Field, Input, Textarea, Select, Toggle,
  SegmentedControl, Thumb, Avatar, EmptyState, Skeleton, useLoading,
  SkeletonStatRow, SkeletonCardGrid, SkeletonTable, SkeletonPanel, SkeletonList,
  Modal, SlideOver, ConfirmModal, Dropdown, Progress, TONES,
  useToast, useMutationWithToast,
} from '../../components/primitives';
import { Wordmark } from '../../layouts/Shell';

// T319 / FR-023 — Prototype fixture importer retired. This bundle is the
// public (pre-auth) sign-in / sign-up / verify / forgot-password / onboarding
// surface; it MUST NOT call any authenticated Convex subscription, and the
// post-WIRE-0 honest-state contract (specs/prd.md §E.3 + honest-state.test.ts
// H5) forbids fabricated fixture-data reads. The previous
// `CIRCA.counts.live` decorative count on the brand panel violated both
// rules and has been replaced with static brand copy that does not claim
// a fact about runtime state. The closure-guard scan defined by
// tests/destub/stub-refs.test.ts (production-tree files only; *.test.*
// files exempt) is now empty for this directory.

// Silence unused-import lint for primitives this file may not reference.
void React; void useState; void useEffect; void useRef; void useCallback; void useMemo;
void Icon; void Btn; void Pill; void CircaTag; void Panel; void PageHead; void Breadcrumbs;
void Tabs; void Spark; void StatCard; void ScoreRing; void HelpDot; void TimerChip;
void AccessGate; void AdminGate; void CapPill; void FreshnessBanner; void AuditTrailLink;
void Field; void Input; void Textarea; void Select; void Toggle; void SegmentedControl;
void Thumb; void Avatar; void EmptyState; void Skeleton; void useLoading;
void SkeletonStatRow; void SkeletonCardGrid; void SkeletonTable; void SkeletonPanel; void SkeletonList;
void Modal; void SlideOver; void ConfirmModal; void Dropdown; void Progress; void TONES;
void useToast; void useMutationWithToast;

/* ============================================================
   CIRCA — Auth & onboarding screens (public, no shell)
   ============================================================ */

function AuthShell({ children, width = 440 }) {
  const navigate = useNavigate();
  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: 'var(--canvas)' }}>
      {/* Brand panel — deep-forest-green (was purple) */}
      <div className="circa-auth-aside" style={{ width: '44%', maxWidth: 620, flexShrink: 0, background: 'linear-gradient(155deg, var(--forest-a), var(--forest-b))',
        padding: '56px 56px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', right: -80, top: -80, width: 380, height: 380, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
        <div style={{ position: 'absolute', left: -60, bottom: 40, width: 220, height: 220, borderRadius: '50%', background: 'rgba(255,255,255,0.03)' }} />
        <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'baseline', gap: 6, fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 500, color: '#fff' }}>
          Circa<span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--live)', alignSelf: 'flex-end', marginBottom: 7 }} />
        </div>
        <div style={{ position: 'relative' }}>
          <h2 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 38, fontWeight: 400, color: '#fff', letterSpacing: '-0.8px', lineHeight: 1.1 }}>Tu canal, al aire 24×7.</h2>
          <p style={{ margin: '18px 0 28px', fontSize: 16, color: 'rgba(255,255,255,0.78)', lineHeight: 1.55, maxWidth: 400 }}>Programa, transmite y monitorea cada canal desde un solo lugar. Sin servidores que administrar.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[['radio', 'Transmisión continua a YouTube o tu propio reproductor'], ['calendar', 'Programación visual con cortes de anuncio'], ['gauge', 'Monitoreo de señal y calidad en vivo']].map(([ic, t]) => (
              <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ width: 32, height: 32, borderRadius: 9, background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name={ic} size={16} color="#fff" /></span>
                <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.9)' }}>{t}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--live)', animation: 'circa-pulse 1.4s infinite' }} />
          Transmisión continua para cada audiencia.
        </div>
      </div>
      {/* Form pane */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 28, padding: '40px 24px' }}>
        <div className="circa-auth-logo" style={{ display: 'none' }}><Wordmark size={30} onClick={() => navigate('/sign-in')} /></div>
        <div style={{ width: '100%', maxWidth: width }}>{children}</div>
      </div>
    </div>
  );
}

export function SignIn() {
  const { signIn } = useAuthActions();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(''); const [pw, setPw] = useState(''); const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // T311 / FR-002 — password flow. Reads the deep-link intent the route-shell
  // gate stashed in location.state.from (Shell.tsx:346) and navigates back to
  // it on success; on rejection surfaces an inline role="alert" error and
  // does NOT navigate (NFR-004 honest-state).
  const submitPassword = async () => {
    setError(null);
    setLoading(true);
    try {
      await signIn('password', { email, password: pw, flow: 'signIn' });
      const from = (location.state as { from?: string } | null)?.from ?? '/';
      navigate(from, { replace: true });
    } catch (e) {
      setLoading(false);
      const msg = e instanceof Error && e.message ? e.message : 'No se pudo iniciar sesión.';
      setError(msg);
    }
  };

  // T311 / FR-002 — SSO is split off the password path. The provider id is a
  // distinct argument to signIn; never call signIn('password', ...) here.
  const submitSSO = (provider: 'google' | 'github') => {
    setError(null);
    void signIn(provider);
  };

  return (
    <AuthShell>
      <Panel pad={0} noBorder style={{ background: 'transparent' }}>
        <h1 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 400, letterSpacing: '-0.3px', lineHeight: 1.2 }}>Inicia sesión en Circa.</h1>
        <p style={{ margin: '8px 0 28px', fontSize: 16, color: 'var(--muted-slate)' }}>Continúa gestionando tus canales.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Field label="Correo de trabajo" required><Input value={email} onChange={setEmail} type="email" placeholder="tu@empresa.com" /></Field>
          <Field label="Contraseña" required><Input value={pw} onChange={setPw} type="password" placeholder="••••••••" /></Field>
          <div style={{ textAlign: 'right', marginTop: -6 }}><a href="/forgot-password" style={{ fontSize: 13, color: 'var(--cta)', textDecoration: 'none' }}>¿Olvidaste tu contraseña?</a></div>
          {error && (
            <p role="alert" style={{ margin: 0, fontSize: 13, color: 'var(--bad)' }}>{error}</p>
          )}
          <Btn variant="solid" size="lg" full loading={loading} onClick={() => { void submitPassword(); }}>Iniciar sesión</Btn>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: 'var(--muted-slate)', fontSize: 13 }}>
            <div style={{ flex: 1, height: 1, background: 'var(--lightest-gray)' }} />o<div style={{ flex: 1, height: 1, background: 'var(--lightest-gray)' }} /></div>
          <Btn variant="ghost" size="lg" full icon="globe" onClick={() => submitSSO('google')}>Continuar con inicio de sesión único (SSO)</Btn>
        </div>
      </Panel>
      <p style={{ margin: 0, fontSize: 14, color: 'var(--muted-slate)' }}>¿No tienes cuenta? <a href="/sign-up" style={{ color: 'var(--cta)', textDecoration: 'none' }}>Crea tu espacio de trabajo</a></p>
    </AuthShell>
  );
}

export function SignUp() {
  const { signIn } = useAuthActions();
  const navigate = useNavigate();
  const [name, setName] = useState(''); const [email, setEmail] = useState(''); const [pw, setPw] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const score = [pw.length >= 8, /[A-Z]/.test(pw), /[a-z]/.test(pw), /\d/.test(pw), /[^A-Za-z0-9]/.test(pw)].filter(Boolean).length;
  const seg = score <= 1 ? 1 : score === 2 ? 2 : score === 3 ? 3 : 4;

  // Password sign-up. The bare @convex-dev/auth Password provider (no
  // `verify` configured in convex/auth.ts) does NOT send a verification
  // email — `signIn('password', { flow: 'signUp' })` resolves with an
  // active session, so we land the user on the dashboard directly.
  // If email verification is later wired (ResendOTP / Resend), this
  // navigates to /verify-email instead and that screen must display the
  // actual signup email rather than the hardcoded prototype copy.
  const submitSignUp = async () => {
    setError(null);
    setLoading(true);
    try {
      await signIn('password', { email, password: pw, flow: 'signUp', name });
      navigate('/');
    } catch (e) {
      setLoading(false);
      const msg = e instanceof Error && e.message ? e.message : 'No se pudo crear el espacio de trabajo.';
      setError(msg);
    }
  };

  return (
    <AuthShell>
      <Panel pad={0} noBorder style={{ background: 'transparent' }}>
        <h1 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 400, letterSpacing: '-0.3px', lineHeight: 1.2 }}>Crea tu espacio de Circa.</h1>
        <p style={{ margin: '8px 0 28px', fontSize: 16, color: 'var(--muted-slate)' }}>30 días gratis. Sin tarjeta.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Field label="Nombre completo" required><Input value={name} onChange={setName} placeholder="Tu nombre" /></Field>
          <Field label="Correo de trabajo" required><Input value={email} onChange={setEmail} type="email" placeholder="tu@empresa.com" /></Field>
          <div>
            <Field label="Contraseña" required><Input value={pw} onChange={setPw} type="password" placeholder="Mínimo 8 caracteres" /></Field>
            <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
              {[0,1,2,3].map(i => <div key={i} style={{ flex: 1, height: 4, borderRadius: 999, background: pw && i < seg ? 'var(--good)' : 'var(--lightest-gray)' }} />)}
            </div>
          </div>
          {error && (
            <p role="alert" style={{ margin: 0, fontSize: 13, color: 'var(--bad)' }}>{error}</p>
          )}
          <Btn variant="solid" size="lg" full loading={loading} onClick={() => { void submitSignUp(); }}>Crear espacio de trabajo</Btn>
        </div>
      </Panel>
      <p style={{ margin: 0, fontSize: 14, color: 'var(--muted-slate)' }}>¿Ya tienes cuenta? <a href="/sign-in" style={{ color: 'var(--cta)', textDecoration: 'none' }}>Inicia sesión</a></p>
    </AuthShell>
  );
}

export function VerifyEmail() {
  const { signIn } = useAuthActions();
  const navigate = useNavigate();
  const [code, setCode] = useState(['','','','','','']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const set = (i: number, v: string) => {
    if (!/^\d?$/.test(v)) return;
    const c = [...code]; c[i] = v; setCode(c);
    if (v && i < 5) refs.current[i+1]?.focus();
  };

  // T311 / FR-002 — Email-verification flow. Convex Auth's Password provider
  // exposes `flow: 'email-verification'` for the OTP code; on success the
  // user is moved into the onboarding step.
  const submitVerify = async () => {
    setError(null);
    setLoading(true);
    const codeStr = code.join('');
    try {
      await signIn('password', { code: codeStr, flow: 'email-verification' });
      navigate('/onboarding');
    } catch (e) {
      setLoading(false);
      const msg = e instanceof Error && e.message ? e.message : 'No se pudo verificar el código.';
      setError(msg);
    }
  };

  return (
    <AuthShell>
      <Panel pad={0} noBorder style={{ background: 'transparent' }}>
        <h1 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 400, letterSpacing: '-0.3px' }}>Revisa tu correo.</h1>
        <p style={{ margin: '10px 0 28px', fontSize: 16, color: 'var(--body-muted)', lineHeight: 1.5 }}>Enviamos un código de seis dígitos a lu•••@canalcirca.tv. Ingrésalo para terminar de registrarte.</p>
        <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
          {code.map((d, i) => <input key={i} ref={el => { refs.current[i] = el; }} value={d} onChange={e => set(i, e.target.value)} maxLength={1}
            style={{ width: 56, height: 56, borderRadius: 8, border: '1px solid var(--border-cool)', textAlign: 'center', fontSize: 24,
              fontFamily: 'var(--font-display)', outline: 'none', color: 'var(--ink)' }} />)}
        </div>
        {error && (
          <p role="alert" style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--bad)' }}>{error}</p>
        )}
        <Btn variant="solid" size="lg" full loading={loading} onClick={() => { void submitVerify(); }}>Verificar y continuar</Btn>
        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 13, color: 'var(--muted-slate)' }}>
          Reenvío de código: próximamente
        </div>
      </Panel>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const { signIn } = useAuthActions();
  const navigate = useNavigate();
  const [sent, setSent] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // T311 / FR-002 — Password reset request. The Convex Auth Password provider
  // uses `flow: 'reset'` to send the reset email. On resolve we move to the
  // honest "check your inbox" confirmation state; a server failure surfaces
  // an inline error instead of a fake success.
  const submitReset = async () => {
    setError(null);
    setLoading(true);
    try {
      await signIn('password', { email, flow: 'reset' });
      setLoading(false);
      setSent(true);
    } catch (e) {
      setLoading(false);
      const msg = e instanceof Error && e.message ? e.message : 'No se pudo enviar el enlace.';
      setError(msg);
    }
  };

  return (
    <AuthShell>
      <Panel pad={0} noBorder style={{ background: 'transparent' }}>
        {!sent ? <>
          <h1 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 400, letterSpacing: '-0.3px' }}>Restablece tu contraseña.</h1>
          <p style={{ margin: '10px 0 28px', fontSize: 16, color: 'var(--body-muted)' }}>Ingresa el correo de tu cuenta de Circa. Te enviaremos un enlace de un solo uso.</p>
          <Field label="Correo"><Input value={email} onChange={setEmail} type="email" placeholder="tu@empresa.com" /></Field>
          {error && (
            <p role="alert" style={{ margin: '12px 0 0', fontSize: 13, color: 'var(--bad)' }}>{error}</p>
          )}
          <Btn variant="solid" size="lg" full style={{ marginTop: 18 }} loading={loading} onClick={() => { void submitReset(); }}>Enviar enlace</Btn>
        </> : <>
          <h1 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 400, letterSpacing: '-0.3px' }}>Revisa tu correo.</h1>
          <p style={{ margin: '10px 0 28px', fontSize: 16, color: 'var(--body-muted)', lineHeight: 1.5 }}>Si ese correo está en una cuenta de Circa, recibirás un enlace en menos de un minuto.</p>
        </>}
        <div style={{ textAlign: 'center', marginTop: 16 }}><Btn variant="text" iconRight="arrowRight" onClick={() => navigate('/sign-in')}>Volver a iniciar sesión</Btn></div>
      </Panel>
    </AuthShell>
  );
}

export function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [broadcast, setBroadcast] = useState<string[]>([]);
  const opts1 = ['Noticias','Música','Deportes','Entretenimiento','Documental','Educación','Otro'];
  const [count, setCount] = useState('2–5');
  const [dest, setDest] = useState<string[]>([]);
  const toggle = (arr: string[], set: (next: string[]) => void, v: string) =>
    set(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);
  const Chip = ({ on, children, onClick }) => <button onClick={onClick} style={{ border: `1px solid ${on ? 'var(--ink-muted)' : 'var(--border-cool)'}`,
    background: on ? 'var(--ink-muted)' : 'transparent', color: on ? '#fff' : 'var(--ink-muted)', borderRadius: 999, padding: '8px 16px', fontSize: 14, cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>{children}</button>;
  return (
    <AuthShell width={720}>
      <Panel pad={0} noBorder style={{ background: 'transparent' }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 28 }}>
          {[1,2,3,4].map(s => <div key={s} style={{ flex: 1, height: 5, borderRadius: 999, background: s <= step ? 'var(--ink)' : 'var(--lightest-gray)' }} />)}
        </div>
        {step === 1 && <>
          <h2 style={{ margin: '0 0 20px', fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 400 }}>¿Qué vas a transmitir?</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>{opts1.map(o => <Chip key={o} on={broadcast.includes(o)} onClick={() => toggle(broadcast, setBroadcast, o)}>{o}</Chip>)}</div>
        </>}
        {step === 2 && <>
          <h2 style={{ margin: '0 0 20px', fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 400 }}>¿Cuántos canales planeas operar?</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>{['1','2–5','6–20','20+'].map(o => <button key={o} onClick={() => setCount(o)} style={{ border: `1px solid ${count === o ? 'var(--deep-green)' : 'var(--border-cool)'}`, background: count === o ? 'var(--pale-green)' : 'transparent', borderRadius: 16, padding: '24px 0', fontSize: 20, fontFamily: 'var(--font-display)', cursor: 'pointer' }}>{o}</button>)}</div>
        </>}
        {step === 3 && <>
          <h2 style={{ margin: '0 0 8px', fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 400 }}>¿A dónde transmites?</h2>
          <p style={{ margin: '0 0 20px', fontSize: 14, color: 'var(--muted-slate)' }}>Solo informativo; el destino real se configura por canal.</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>{['YouTube Live','Mi propio reproductor o sitio','Ambos'].map(o => <Chip key={o} on={dest.includes(o)} onClick={() => toggle(dest, setDest, o)}>{o}</Chip>)}</div>
        </>}
        {step === 4 && <>
          <h2 style={{ margin: '0 0 8px', fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 400 }}>Crea tu primer canal</h2>
          <p style={{ margin: '0 0 20px', fontSize: 14, color: 'var(--muted-slate)' }}>Un canal es una transmisión 24×7. Puedes ajustarlo después.</p>
          <Field label="Nombre del canal"><Input placeholder="Mi primer canal" /></Field>
        </>}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 32 }}>
          <Btn variant="ghost" onClick={() => step > 1 ? setStep(step - 1) : navigate('/sign-in')}>{step > 1 ? 'Atrás' : 'Cancelar'}</Btn>
          <Btn variant="solid" iconRight="arrowRight" onClick={() => step < 4 ? setStep(step + 1) : navigate('/')}>{step < 4 ? 'Continuar' : 'Entrar a Circa'}</Btn>
        </div>
      </Panel>
    </AuthShell>
  );
}

